<template>
    <div class="sharing">
        <Box
            title="Páginas de Compartilhamento"
            sub-title="Gerencie todas as suas páginas de compartilhamento de arquivos"
            icon="share"
            :variant="BOX_VARIANTS.DEFAULT"
            :actions="headerActions"
            @action-click="handleHeaderAction"
        >

            <!-- Cards de estatísticas -->
            <div class="sharing_stats_section">
                <div class="row">
                    <!-- Total de Páginas -->
                    <div class="grid_col_3 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="sharing_stat_card sharing_stat_card--primary">
                            <div class="sharing_stat_header">
                                <div class="sharing_stat_icon">
                                    <sw-icon name="share" />
                                </div>
                                <div class="sharing_stat_info">
                                    <span class="sharing_stat_label">Total de Páginas</span>
                                    <div class="sharing_stat_numbers">
                                        <span class="sharing_stat_main">{{ totalShares }}</span>
                                        <span class="sharing_stat_secondary">páginas criadas</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Páginas Ativas -->
                    <div class="grid_col_3 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="sharing_stat_card sharing_stat_card--success">
                            <div class="sharing_stat_header">
                                <div class="sharing_stat_icon">
                                    <sw-icon name="checkmark" />
                                </div>
                                <div class="sharing_stat_info">
                                    <span class="sharing_stat_label">Páginas Ativas</span>
                                    <div class="sharing_stat_numbers">
                                        <span class="sharing_stat_main">{{ activeSharesCount }}</span>
                                        <span class="sharing_stat_secondary">de {{ totalShares }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Total de Acessos -->
                    <div class="grid_col_3 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="sharing_stat_card sharing_stat_card--compact">
                            <div class="sharing_stat_header">
                                <div class="sharing_stat_icon">
                                    <sw-icon name="point-up" />
                                </div>
                                <div class="sharing_stat_info">
                                    <span class="sharing_stat_label">Total de Acessos</span>
                                    <div class="sharing_stat_numbers">
                                        <span class="sharing_stat_main">{{ totalViews }}</span>
                                        <span class="sharing_stat_secondary">este mês</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Downloads -->
                    <div class="grid_col_3 grid_col_m_6 grid_col_s_12 grid_col_xs_12">
                        <div class="sharing_stat_card sharing_stat_card--compact">
                            <div class="sharing_stat_header">
                                <div class="sharing_stat_icon">
                                    <sw-icon name="cloud" />
                                </div>
                                <div class="sharing_stat_info">
                                    <span class="sharing_stat_label">Downloads</span>
                                    <div class="sharing_stat_numbers">
                                        <span class="sharing_stat_main">{{ totalDownloads }}</span>
                                        <span class="sharing_stat_secondary">este mês</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Seção de Filtros -->
            <FilterSection
                label="Filtrar por:"
                search-placeholder="Buscar páginas de compartilhamento..."
                :search-value="searchQuery"
                :filters="filterOptions"
                :filter-values="filterValues"
                @update:search="searchQuery = $event"
                @update:filter="updateFilter"
                @clear-filters="clearFilters"
            />

        </Box>

        <!-- Loading State -->
        <div class="sharing_loading_section" v-if="loadingShares">
            <app-spinner text="Carregando páginas de compartilhamento..." />
        </div>

        <!-- Lista de compartilhamentos -->
        <div class="sharing_content" v-else-if="filteredShares.length > 0">
            <div class="row">
                <div
                    v-for="share in filteredShares"
                    :key="share.id"
                    class="grid_col_12"
                >
                    <ShareLinkCard
                        :item="share"
                        @view="viewShare"
                        @edit="editShare"
                        @delete="deleteShare"
                        @copy="handleCopyLink"
                    />
                </div>
            </div>
        </div>

        <!-- Estado vazio -->
        <div class="sharing_empty_section" v-else-if="shares.length === 0">
            <EmptyState
                icon="share"
                title="Nenhuma página criada"
                description="Crie sua primeira página de compartilhamento para começar a compartilhar arquivos"
            >
                <template #actions>
                    <sw-button
                        name="Criar Primeira Página"
                        color="blue"
                        icon="plus"
                        @click="addNewShare"
                    />
                </template>
            </EmptyState>
        </div>

        <!-- Nenhum resultado -->
        <div class="sharing_no_results_section" v-else>
            <EmptyState
                icon="search"
                title="Nenhum resultado encontrado"
                description="Tente ajustar os filtros de busca ou usar termos diferentes"
            >
                <template #actions>
                    <sw-button
                        name="Criar Nova Página"
                        color="blue"
                        icon="plus"
                        @click="addNewShare"
                    />
                </template>
            </EmptyState>
        </div>

        <!-- Modal de Edição -->
        <sw-modal
            :visible="showEditModal"
            @update="closeEditModal"
        >
            <template #header>
                <div class="editShareModal_header">
                    <div class="editShareModal_icon">
                        <sw-icon name="pencil" />
                    </div>
                    <div class="editShareModal_title">
                        <h3>Editar Compartilhamento</h3>
                        <span>Gerencie as configurações do seu link de compartilhamento</span>
                    </div>
                </div>
            </template>

            <template #content>
                <EditShareModal
                    :share-data="selectedShare"
                    @close="closeEditModal"
                    @save="handleSaveShare"
                    @copy="handleCopyLink"
                />
            </template>
        </sw-modal>

        <!-- Modal de Confirmação de Exclusão -->
        <sw-modal
            :visible="showDeleteModal"
            @update="showDeleteModal = false"
        >
            <template #header>
                <ModalHeader
                    :icon="DELETE_MODAL_CONFIG.ICON"
                    :title="DELETE_MODAL_CONFIG.TITLE"
                    :subtitle="DELETE_MODAL_CONFIG.SUBTITLE"
                    :color="DELETE_MODAL_CONFIG.COLOR"
                />
            </template>

            <template #content>
                <ConfirmDeleteModal
                    :item-name="shareToDelete?.title || ''"
                    :details="shareToDelete"
                    :loading="deletingShare"
                    @confirm="confirmDeleteShare"
                    @cancel="cancelDeleteShare"
                />
            </template>
        </sw-modal>

        <!-- Modal Criar Nova Página - showCreateModal -->
        <sw-modal
            :visible="showCreateModal"
            @update="closeCreateModal"
        >
            <template #header>
                <ModalHeader
                    icon="plus"
                    title="Criar Nova Página de Compartilhamento"
                    subtitle="Configure uma nova página para seus documentos"
                    color="blue"
                />
            </template>

            <template #content>
                <CreateShareModal
                    @close="closeCreateModal"
                    @save="handleCreateShare"
                />
            </template>
        </sw-modal>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';

    import { fetchShares } from '@/services/mockApi';
    import { DELETE_MODAL_CONFIG, DELETE_MODAL_MESSAGES, BOX_VARIANTS } from '@/constants';

    import Box from '@Component/boxs/BoxComponent';
    import ShareLinkCard from '@Component/webPages/ShareLinkCard';
    import WebLinkEmptyState from '@Component/webPages/WebLinkEmptyState';
    import EditShareModal from '@Component/webPages/EditShareModal';
    import CreateShareModal from '@Component/webPages/CreateShareModal';
    import ModalHeader from '@Component/modals/ModalHeader';
    import ConfirmDeleteModal from '@Component/webPages/ConfirmDeleteModal';
    import FilterSection from '@Component/filters/FilterSection';
    import EmptyState from '@Component/empty-states/EmptyState';

    const loadingShares = ref(false);
    const shares = ref([]);
    const error = ref(null);
    const searchQuery = ref('');

    // Estado do modal de criação
    const showCreateModal = ref(false);

    // Estado do modal de edição
    const showEditModal = ref(false);
    const selectedShare = ref(null);

    // Estado do modal de exclusão
    const showDeleteModal = ref(false);
    const shareToDelete = ref(null);
    const deletingShare = ref(false);

    // Ações do header
    const headerActions = computed(() => [
        {
            type: 'button',
            icon: 'plus',
            text: 'Nova Página',
            title: 'Criar nova página de compartilhamento',
            key: 'create'
        }
    ]);

    // Configuração dos filtros para o FilterSection
    const filterOptions = ref([
        {
            key: 'status',
            label: 'Status',
            options: [
                { value: '', label: 'Todos os Status' },
                { value: 'active', label: 'Ativo' },
                { value: 'inactive', label: 'Inativo' }
            ]
        },
        {
            key: 'type',
            label: 'Tipo',
            options: [
                { value: '', label: 'Todos os Tipos' },
                { value: 'public', label: 'Público' },
                { value: 'private', label: 'Privado' },
                { value: 'password', label: 'Com Senha' }
            ]
        }
    ]);

    const filterValues = ref({
        status: '',
        type: ''
    });

    // Computed para estatísticas
    const totalShares = computed(() => shares.value.length);

    const activeSharesCount = computed(() => {
        return shares.value.filter(share => share.active).length;
    });

    const totalViews = computed(() => {
        return shares.value.reduce((total, share) => total + (share.views || 0), 0);
    });

    const totalDownloads = computed(() => {
        return shares.value.reduce((total, share) => total + (share.downloads || 0), 0);
    });


    // Computed para filtros
    const filteredShares = computed(() => {
        let filtered = shares.value;

        if (searchQuery.value) {
            filtered = filtered.filter(share =>
                share.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                share.description?.toLowerCase().includes(searchQuery.value.toLowerCase())
            );
        }

        if (filterValues.value.status) {
            filtered = filtered.filter(share => {
                if (filterValues.value.status === 'active') return share.active;
                if (filterValues.value.status === 'inactive') return !share.active;
                return true;
            });
        }

        if (filterValues.value.type) {
            filtered = filtered.filter(share => share.type === filterValues.value.type);
        }

        return filtered;
    });

    const loadShares = async () => {
        try {
            loadingShares.value = true;
            error.value = null;

            const response = await fetchShares();

            if (response.status === 'success') {
                shares.value = response.data;
            } else {
                error.value = response.error.message;
                console.error('Erro ao carregar compartilhamentos:', response.error);
            }
        } catch (err) {
            error.value = 'Erro de conexão. Tente novamente.';
            console.error('Erro na requisição de compartilhamentos:', err);
        } finally {
            loadingShares.value = false;
        }
    };

    onMounted(() => {
        loadShares();
    });

    // Métodos para filtros
    const updateFilter = ({ key, value }) => {
        filterValues.value[key] = value;
    };

    const clearFilters = () => {
        searchQuery.value = '';
        filterValues.value = {
            status: '',
            type: ''
        };
    };

    const handleAction = (action) => {
        console.log('Action clicada:', action);
        if (action.click === 'createNewPage') {
            createNewPage();
        }
    };

    const handleHeaderAction = (action) => {
        console.log('Action received:', action);
        if (action.key === 'create') {
            createNewPage();
        }
    };

    const createNewPage = () => {
        showCreateModal.value = true;
    };

    const addNewShare = () => {
        showCreateModal.value = true;
    };

    // Funções do modal de criação
    const closeCreateModal = () => {
        showCreateModal.value = false;
    };

    const handleCreateShare = async (newShareData) => {
        try {
            console.log('Criando nova página de compartilhamento:', newShareData);

            // Adicionar à lista local
            shares.value.unshift(newShareData);

            console.log('✅ Página de compartilhamento criada com sucesso');

        } catch (error) {
            console.error('❌ Erro ao criar página:', error);
            alert('Erro ao criar página. Tente novamente.');
        }
    };

    const handleCopyLink = (copyData) => {
        console.log('Link copiado:', copyData);
        // Aqui você pode adicionar lógica adicional se necessário
    };

    // Funções do modal de edição
    const closeEditModal = () => {
        showEditModal.value = false;
        selectedShare.value = null;
    };

    const handleSaveShare = async (updatedShare) => {
        try {
            console.log('Salvando alterações do compartilhamento:', updatedShare);

            // Atualizar o compartilhamento na lista local
            const index = shares.value.findIndex(share => share.id === updatedShare.id);
            if (index !== -1) {
                shares.value[index] = { ...updatedShare };
            }

            // Fechar modal
            closeEditModal();

            // Mostrar feedback de sucesso
            console.log('✅ Compartilhamento atualizado com sucesso');
            alert('Compartilhamento atualizado com sucesso!');

        } catch (error) {
            console.error('❌ Erro ao salvar compartilhamento:', error);
            alert('Erro ao salvar alterações. Tente novamente.');
        }
    };

    const viewShare = (share) => {
        console.log('Visualizar compartilhamento:', share);
        // Simula redirecionamento para localhost com base nos dados do compartilhamento
        const companyName = getCompanyNameFromShare(share);
        const urlCustom = getUrlCustomFromShare(share);
        let localUrl = `/share/${companyName}/${urlCustom}`;

        // Adiciona parâmetro protected=true se o item requer autenticação
        if (share.settings?.requireAuth) {
            localUrl += '?protected=true';
        }

        console.log('Redirecionando para:', localUrl);
        window.open(localUrl, '_blank');
    };

    // Função para extrair nome da empresa do compartilhamento
    const getCompanyNameFromShare = (share) => {
        // Extrai o nome da empresa do título ou cria um baseado no ID
        if (share.title.includes('TechCorp')) {
            return 'techcorp';
        } else if (share.title.includes('João Silva')) {
            return 'joao-silva';
        } else if (share.title.includes('Maria Santos')) {
            return 'maria-santos';
        } else if (share.title.includes('Carlos Oliveira')) {
            return 'carlos-oliveira';
        } else if (share.title.includes('Ana Costa')) {
            return 'ana-costa';
        } else {
            return `company-${share.id}`;
        }
    };

    // Função para extrair URL customizada do compartilhamento
    const getUrlCustomFromShare = (share) => {
        // Cria uma URL amigável baseada no título
        if (share.title.includes('Documentos Pessoais')) {
            return 'documentos-pessoais';
        } else if (share.title.includes('Contratos Empresariais')) {
            return 'contratos-2024';
        } else if (share.title.includes('Documentos Vencidos')) {
            return 'docs-vencidos';
        } else if (share.title.includes('Comprovantes de Residência')) {
            return 'comprovantes';
        } else if (share.title.includes('Documentos de Viagem')) {
            return 'viagem-eua';
        } else {
            return `share-${share.id}`;
        }
    };

    const editShare = (share) => {
        console.log('Editar compartilhamento:', share);
        console.log('Abrindo modal de edição...');
        selectedShare.value = share;
        showEditModal.value = true;
        console.log('showEditModal.value:', showEditModal.value);
    };

    const deleteShare = (share) => {
        console.log('Excluir compartilhamento:', share);
        shareToDelete.value = share;
        showDeleteModal.value = true;
    };

    const confirmDeleteShare = async () => {
        if (!shareToDelete.value) return;

        try {
            deletingShare.value = true;

            // Simular chamada de API
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Remover da lista local
            const index = shares.value.findIndex(share => share.id === shareToDelete.value.id);
            if (index !== -1) {
                shares.value.splice(index, 1);
            }

            // Fechar modal e limpar estado
            showDeleteModal.value = false;
            shareToDelete.value = null;

            // Mostrar mensagem de sucesso
            console.log('✅ Compartilhamento excluído com sucesso');
            alert(DELETE_MODAL_MESSAGES.SUCCESS);

        } catch (error) {
            console.error('❌ Erro ao excluir compartilhamento:', error);
            alert(DELETE_MODAL_MESSAGES.ERROR);
        } finally {
            deletingShare.value = false;
        }
    };

    const cancelDeleteShare = () => {
        showDeleteModal.value = false;
        shareToDelete.value = null;
        deletingShare.value = false;
    };

</script>

<style lang="scss" scoped>
@import '@/assets/css/mediaScreen.scss';

.sharing {}

// Seção de estatísticas de compartilhamento
.sharing_stats_section {
    margin-bottom: 2em;

    .row {
        align-items: stretch;
    }
}

.sharing_stat_card {
    background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
    border-radius: 1.25em;
    padding: 1.5em;
    border: 1px solid rgba(226, 232, 240, 0.8);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, transparent, var(--linkColor), transparent);
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    &:hover {
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.08),
            0 1px 2px rgba(0, 0, 0, 0.05);
        border-color: rgba(59, 130, 246, 0.2);

        &::before {
            opacity: 1;
        }
    }
}

.sharing_stat_header {
    display: flex;
    align-items: flex-start;
    gap: 1.25em;
    margin-bottom: 1.25em;
    flex: 1;
}

.sharing_stat_icon {
    width: 3.25em;
    height: 3.25em;
    border-radius: 1em;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125em;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    position: relative;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05));
        pointer-events: none;
    }
}

.sharing_stat_info {
    flex: 1;
}

.sharing_stat_label {
    font-size: 0.875em;
    color: rgba(100, 116, 139, 0.8);
    font-weight: 600;
    margin-bottom: 0.5em;
    display: block;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.sharing_stat_numbers {
    display: flex;
    align-items: baseline;
    gap: 0.5em;
    margin-bottom: 0.25em;
}

.sharing_stat_main {
    font-size: 2.25em;
    font-weight: 800;
    color: var(--textPrimary);
    line-height: 1;
}

.sharing_stat_secondary {
    font-size: 0.9em;
    color: rgba(100, 116, 139, 0.7);
    font-weight: 500;
}

// Cards compactos de estatísticas
.sharing_stat_card--compact {
    padding: 1.25em;

    .sharing_stat_header {
        margin-bottom: 1em;
    }

    .sharing_stat_icon {
        width: 2.75em;
        height: 2.75em;
        font-size: 1em;
    }

    .sharing_stat_label {
        font-size: 0.8em;
        margin-bottom: 0.375em;
    }

    .sharing_stat_numbers {
        gap: 0.375em;
    }

    .sharing_stat_main {
        font-size: 1.75em;
        font-weight: 700;
    }

    .sharing_stat_secondary {
        font-size: 0.8em;
    }
}

// Variações de cores dos cards
.sharing_stat_card--primary .sharing_stat_icon {
    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    color: white;
}

.sharing_stat_card--success .sharing_stat_icon {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
}


// Seção de conteúdo
.sharing_content {
    background: var(--backgroundColorLighter);
    border-radius: 1.5em;
    padding: 25px 0px;
    border: 1px solid var(--borderColorLight);
}

// Estado de loading
.sharing_loading_section {
    background: var(--backgroundColorLighter);
    border-radius: 1.5em;
    border: 1px solid var(--borderColorLight);
    text-align: center;
    padding: 4em 2em;
    display: flex;
    justify-content: center;
    align-items: center;
}

// Estados vazios (usando EmptyState component)
.sharing_empty_section,
.sharing_no_results_section {
    background: var(--backgroundColorLighter);
    border-radius: 1.5em;
    border: 1px solid var(--borderColorLight);
}

    /* ===========================================
       MODAL HEADER ESPECÍFICO (LEGACY)
       =========================================== */

    /* Edit Share Modal Header Styles - Mantido para compatibilidade */
    .editShareModal_header {
        display: flex;
        align-items: center;
        gap: 1em;
        padding: 0;
    }

    .editShareModal_icon {
        width: 2em;
        height: 2em;
        border-radius: 4px;
        background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25em;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }

    .editShareModal_title {
        h3 {
            font-size: 1.25em;
            font-weight: 700;
            color: var(--textPrimary, #1f2937);
        }

        span {
            font-size: 0.875em;
            color: var(--textSecondary, #64748b);
            line-height: 1.4;
        }
    }

    /* Development Modal Styles */
    .developmentContent {
        display: flex;
        gap: 1.5em;
        align-items: flex-start;
        margin-bottom: 2em;
    }

    .developmentContent_icon {
        width: 4em;
        height: 4em;
        border-radius: 1em;
        background: linear-gradient(135deg, var(--colorOrange, #f59e0b), var(--colorOrangeDark, #d97706));
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.75em;
        box-shadow: 0 8px 20px rgba(245, 158, 11, 0.3);
        flex-shrink: 0;
    }

    .developmentContent_info {
        flex: 1;

        p {
            font-size: 1em;
            color: var(--textSecondary);
            margin-bottom: 1.5em;
            line-height: 1.6;
        }
    }

    .developmentContent_features {
        h4 {
            font-size: 1em;
            font-weight: 600;
            color: var(--textPrimary);
            margin-bottom: 1em;
        }

        ul {
            list-style: none;
            padding: 0;
            margin: 0;

            li {
                display: flex;
                align-items: center;
                gap: 0.75em;
                padding: 0.625em 0;
                font-size: 0.9375em;
                color: var(--textSecondary);
                border-bottom: 1px solid var(--borderColorLight, #e5e7eb);

                &:last-child {
                    border-bottom: none;
                }

                &:before {
                    content: '';
                    width: 0.5em;
                    height: 0.5em;
                    border-radius: 50%;
                    background: var(--colorBlue, #3b82f6);
                    flex-shrink: 0;
                }
            }
        }
    }

    /* Modal styles override */
    #modal_developmentModal {
        .sw_modal_container {
            max-width: 600px !important;
        }
    }

    @include screen_medias(s, m) {
        .developmentContent {
            flex-direction: column;
            text-align: center;
        }

        .developmentContent_icon {
            align-self: center;
        }
    }
</style>